<script lang="ts" setup>
import { computed, ref } from "vue"
import { useEmConsoCompareStore } from "@modules/energy-manager/stores/em-conso-compare.store"
import { METER_TYPES_LIST, SOURCES_TYPES } from "@core/constants"
import { VARIABLES } from "@modules/energy-manager/constants"
import { ColumnConfig } from "@types"
import { formatIdSource, formatNumber } from "@core/helpers"
import { computedAndFormatEvolution } from "@modules/energy-manager/helpers"

//===============================================
// Gestion du tableau
//===============================================
const emConsoCompareStore = useEmConsoCompareStore()
/**
 * Liste des colonnes du tableau
 */
const getColumns = computed<ColumnConfig[]>(() => {
    const columns = [
        {
            field: "building",
            header: "Nom",
            sortable: true,
            link: true,
            style: { width: "8rem" },
        },
        {
            field: "address",
            header: "Adresses",
            sortable: true,
            link: true,
            style: { width: "8rem" },
        },
        {
            field: "source",
            header: "Sources",
            sortable: true,
            subLink: (value: { source: number }) => {
                return value.source > 0
            },
            style: { width: "5rem" },
        },
        {
            field: "folder",
            header: "Dossier",
            sortable: true,
            link: true,
            style: { width: "7rem" },
        },
        {
            field: "tag",
            header: "Étiquettes",
            style: { width: "10rem" },
        },
    ] as ColumnConfig[]

    if (emConsoCompareStore.compareVariable) {
        // split le nom pour ne récupérer que le nom de la variable
        const splitVariable = emConsoCompareStore.compareVariable.split(".")[0]
        columns.splice(
            columns.length - 1,
            0,
            {
                field: "variables",
                header: `${VARIABLES[splitVariable]?.name} (${VARIABLES[splitVariable]?.unit})`,
                sortable: true,
                style: { width: "10em" },
            },
            {
                field: "evolution",
                header: "Évolution",
                sortable: true,
                style: { width: "9em" },
            }
        )
    }

    return columns
})
/**
 * récupère la liste des bâtiments à afficher dans le tableau
 */
const getValues = computed(() => {
    const splitVariable = (emConsoCompareStore.compareVariable ?? "")?.split(
        "."
    )?.[0]

    const variableConfig = VARIABLES[splitVariable]
    const maxDigit = parseInt(variableConfig.format?.match(/\d+/)?.[0] ?? "1")
    const unit = variableConfig?.unit ?? ""
    return emConsoCompareStore.getComparedBuildingsList.map((building) => {
        const row = {
            building: building.name,
            address: building.address?.inline_name,
            folder: building.folder?.name,
            source: building.sources.filter(
                (source) =>
                    !["idopenmeteo", "idmeteofrance"].includes(source.idtype)
            ).length,
            tag: building.tags?.map((tag) => tag.name),
            _id_building: building._id_building,
            _id_folder: building.folder?._id_folder,
            sources: building.sources,
        } as Record<string, any>
        // Si on a eu une erreur lors de la récupération des données de comparaison alors on retourne la
        // ligne sans calculer l'évolution
        if (emConsoCompareStore.fetchCompareGraphByBuildingIds.hasError) {
            return row
        }
        // Donnée de la seconde période (période actuelle)
        const firstData =
            emConsoCompareStore.compareData?.[1]?.[building._id_building]

        // Donnée de la première période (période comparée)
        const secondData =
            emConsoCompareStore.compareData?.[0]?.[building._id_building]

        const [evolution, evolutionFormated] = computedAndFormatEvolution(
            firstData,
            secondData,
            { maxDigit, unit }
        )

        const compareDataIndex =
            emConsoCompareStore.compareData.length > 1 ? 1 : 0
        const buildingId = building._id_building

        const dataValue =
            emConsoCompareStore.compareData?.[compareDataIndex]?.[buildingId] ??
            null
        row["evolution"] = evolution
        row["evolutionFormated"] = evolutionFormated
        row["variables"] = dataValue
        row["ranking_order"] = VARIABLES[splitVariable]?.ranking_order
        row["variablesFormated"] = formatNumber(dataValue, {
            unit: VARIABLES[splitVariable]?.unit ?? "",
            maximumFractionDigits: maxDigit,
        })

        return row
    })
})

// ============================================
// Gestion des redirections
// ============================================
/**
 * Quand on clique sur une cellule du tableau
 * @param data - Données de la cellule
 * @param field - Nom du champ
 * @param _blank - Ouvrir dans un nouvel onglet
 */
function onClickCell({
    data,
    field,
    _blank,
}: {
    data: any
    field: string
    _blank?: boolean
}) {
    if (["building", "address"].includes(field)) {
        emConsoCompareStore.clickLink(data._id_building, "building", _blank)
    } else if (field === "folder") {
        emConsoCompareStore.clickLink(data._id_folder, "folder", _blank)
    } else if (field === "location") {
        emConsoCompareStore.clickLink(data._id_folder_parent, "folder", _blank)
    } else if (field === "source") {
        emConsoCompareStore.clickLink(data._id_source, "source", _blank)
    }
}

// ============================================
// Gestion des sous tableaux
// ============================================
const selectedRow = ref()

function onClickPopup({ data, field }: { data: any; field: string }) {
    selectedRow.value = {
        ...data,
        type: field,
    }
}
/**
 * Récupère le détail des sous tableaux
 * en fonction de l'élément sélectionné.
 */
const getDataPopupTable = computed<{
    value: any[]
    columns: ColumnConfig[]
}>(() => {
    if (!selectedRow.value) return { value: [], columns: [] }
    // si on visualise un tableau de source
    return {
        value: selectedRow.value.sources
            // retire les stations météos
            .filter(
                (source: { idtype: string }) =>
                    !["idopenmeteo", "idmeteofrance"].includes(source.idtype)
            )
            .map(
                (source: {
                    idtype: string
                    idsource: string
                    _id_source: string
                    segment: string
                }) => {
                    let type = "Capteur"

                    if (METER_TYPES_LIST.includes(source.idtype)) {
                        type = source.idtype.toUpperCase()
                    }

                    if (source.idtype === SOURCES_TYPES.PDL) {
                        // ajoute le segment
                        type =
                            `${type} ${source.segment ? `(${source.segment})` : ""}`.trim()
                    }

                    return {
                        type,
                        idtype: source.idtype,
                        source: formatIdSource(source.idsource, source.idtype),
                        _id_source: source._id_source,
                    }
                }
            ),
        columns: [
            { field: "type", header: "Type", style: { width: "8rem" } },
            {
                field: "source",
                header: "Identifiant",
                sortable: true,
                link: true,
                style: { width: "13rem" },
            },
        ],
    }
})

/**
 * Détermine le type d'icône à afficher pour les sources
 */
function getIconBySourceType(type: string) {
    switch (type.toLowerCase()) {
        case "pdl":
        case "elecsavoie":
            return {
                icon: "fa-light fa-bolt",
                color: "#EEC137",
            }
        case "pce":
            return {
                icon: "fa-light fa-fire",
                color: "#FA8E42",
            }
        default:
            return {
                icon: "fa-light fa-sensor",
                color: "#35C4DC",
            }
    }
}

const buildingIdEditedForTags = ref<string | null>(null)
const visibleTagsModal = ref(false)
function onClickAddTags(_id_building: string) {
    buildingIdEditedForTags.value = _id_building
    visibleTagsModal.value = true
}

const table = ref()

function onShowTooltipEvolution(index: number, field: string, data: any) {
    const value = table.value?.showTooltip(
        index,
        field,
        data?.["evolutionFormated"]
    )
    if (!value) return ""

    return value?.value
}
</script>
<template>
    <div>
        <BaseTable
            class="list-building"
            :columns="getColumns"
            data-key="_id_building"
            :value="getValues"
            paginator
            scrollable
            search
            :size="'small'"
            :rows="5"
            ref="table"
            :rows-per-page-options="[5, 10, 20, 50]"
            @click:popup="onClickPopup"
            @click:cell="onClickCell"
            teleport-search="#card-building-search"
        >
            <template #popup>
                <BaseTable
                    v-if="selectedRow"
                    :value="getDataPopupTable.value"
                    :columns="getDataPopupTable.columns"
                    data-key="_id_source"
                    class="list-building__popup"
                    scrollable
                    :paginator="getDataPopupTable.value.length > 5"
                    small-paginator
                    @click:cell="onClickCell"
                >
                    <template #cell_type="{ data: { idtype, type } }">
                        <div class="flex items-center gap-1">
                            <BaseIcon
                                v-if="getIconBySourceType(idtype).icon"
                                :icon="getIconBySourceType(idtype).icon"
                                :color="getIconBySourceType(idtype).color"
                                size="sm"
                                class="mr-2"
                            />
                            <p>
                                {{ type }}
                            </p>
                        </div>
                    </template>
                </BaseTable>
            </template>
            <template #cell_tag="{ data: { tag, _id_building } }">
                <ListBuildingTags
                    :tags="tag"
                    @click:add="onClickAddTags(_id_building)"
                ></ListBuildingTags>
            </template>
            <template #cell_variables="{ data }">
                <p>{{ data.variablesFormated }}</p>
            </template>
            <template #cell_evolution="{ data, index, column }">
                <p
                    v-if="data.evolution !== null"
                    class="text-sm base-table-link-label cursor-pointer"
                    v-tooltip.top="
                        onShowTooltipEvolution(index, column.field, data)
                    "
                    :style="
                        data.evolution
                            ? {
                                  color:
                                      data.evolution > 0
                                          ? data.ranking_order === 'desc'
                                              ? 'var(--p-red-500)'
                                              : 'var(--p-teal-500)'
                                          : data.ranking_order === 'desc'
                                            ? 'var(--p-teal-500)'
                                            : 'var(--p-red-500)',
                              }
                            : null
                    "
                >
                    {{ data.evolutionFormated }}
                </p>
            </template>
        </BaseTable>
        <TagsModal
            v-if="buildingIdEditedForTags"
            :building-id="buildingIdEditedForTags"
            v-model:visible="visibleTagsModal"
        ></TagsModal>
    </div>
</template>
<style scoped>
/* Responsive: Écrans de 768px et plus */
@media only screen and (min-width: 768px) {
    .list-building__header {
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        & .p-datatable.p-datatable-sm .p-datatable-tbody > tr > td {
            background-color: red !important;
            color: #333 !important;
        }
    }

    & .list-building__header-inputs {
        flex-direction: row;
        gap: 1rem;
        width: auto;
    }
}

.list-building__list-column {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
}

.list-building__cell-variables {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}
</style>
